import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Button, IconButton, Tooltip, Link, Stack, Chip} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import InlineToggleCell from '../../components/InlineToggleCell';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const summarize = (items) => (items || []).map((item, i) => (
    <Chip key={i} size="small" variant="outlined" label={`${item.action} ${item.entity}`} sx={{mr: 0.5, mb: 0.5}}/>
));

const LevelPlayPlacementsPage = () => {
    const navigate = useNavigate();
    const [placements, setPlacements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchPlacements = useCallback(() => {
        setLoading(true);
        apiService.get('/api/levelplay-placements')
            .then(data => setPlacements(data))
            .catch(err => setError(err.message || 'Failed to fetch placements'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchPlacements(); }, [fetchPlacements]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/levelplay-placements/${itemToDelete.id}`);
            fetchPlacements();
        } catch (err) {
            setError('Failed to delete placement.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {accessorKey: 'placementName', header: 'Placement Name', size: 220,
            Cell: ({cell, row}) => <Link component={RouterLink} to={`/levelplay-placements/${row.original.id}`}>{cell.getValue()}</Link>},
        {id: 'enabled', header: 'Enabled', size: 110,
            Cell: ({row}) => (
                <InlineToggleCell
                    protectedBy={LevelPlayPlacementsPage}
                    protectAction="update"
                    value={row.original.enabled}
                    save={(next) => apiService.put(`/api/levelplay-placements/${row.original.id}/enabled`, {value: next})}
                    onSaved={fetchPlacements}
                    labels={{on: 'Enabled', off: 'Disabled'}}
                />
            )},
        {id: 'award', header: 'Award', size: 260,
            Cell: ({row}) => <Stack direction="row" flexWrap="wrap">{summarize(row.original.award)}</Stack>},
    ], [fetchPlacements]);

    const table = useMaterialReactTable({
        columns, data: placements,
        enablePagination: false, enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {showGlobalFilter: true, sorting: [{id: 'placementName', desc: false}]},
        renderTopToolbarCustomActions: () => (
            <Protected component={LevelPlayPlacementsPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('/levelplay-placements/new')}>
                    Create Placement
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/levelplay-placements/${row.original.id}`}><EditIcon/></IconButton>
                </Tooltip>
                <Protected component={LevelPlayPlacementsPage} action="delete">
                    <Tooltip title="Delete (disable)">
                        <IconButton color="error" onClick={() => { setItemToDelete(row.original); setConfirmOpen(true); }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>LevelPlay Placements</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={LevelPlayPlacementsPage} action="delete">
                <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteItem}
                    title={`Delete Placement: ${itemToDelete?.placementName}?`}
                    message="This disables the placement (the ad callback will no longer award for it) rather than deleting it outright."/>
            </Protected>
        </Box>
    );
};

LevelPlayPlacementsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default LevelPlayPlacementsPage;
